import React, { useState } from "react";
import { useParams } from "react-router-dom";

import {
    Typography,
    Grid,
    Button,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Box
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";

import DetailsView from "../Common/Views/DetailsView";

import stringUtils from "../utils/stringUtils";

function VehicleDataLayout({ data }) {
    const [openDialog, setOpenDialog] = useState(false);

    return (
        <>

            {/* Ownership history dialog */}
            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                aria-labelledby="ownership-history-dialog-title"
                aria-describedby="ownership-history-dialog-description"
            >
                <DialogTitle id="ownership-history-dialog-title">
                    Ownership History
                </DialogTitle>
                <DialogContent dividers>
                    {data.previousOwners && data.previousOwners.length > 0 ? (
                        data.previousOwners.map((owner, index) => (
                            <Box key={index} sx={{ mb: 2 }}>
                                <Typography variant="subtitle1">
                                    {owner.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Phone: {owner.phoneNumber || "-"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Email: {owner.email || "-"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Owned From: {stringUtils.isoToLocalDateTime(owner.ownershipStartDate) || "-"} To: {stringUtils.isoToLocalDateTime(owner.ownershipEndDate) || "-"}
                                </Typography>
                                {index < data.previousOwners.length - 1 && <Divider sx={{ my: 2 }} />}
                            </Box>
                        ))
                    ) : (
                        <DialogContentText id="ownership-history-dialog-description">
                            No previous owners found.
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Vehicle details */}
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} lg={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Registration number
                    </Typography>
                    <Typography variant="body2">{data.registrationNumber || "-"}</Typography>
                </Grid>

                <Grid item xs={12} sm={6} lg={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Make
                    </Typography>
                    <Typography variant="body2">{data.make || "-"}</Typography>
                </Grid>

                <Grid item xs={12} sm={6} lg={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Model
                    </Typography>
                    <Typography variant="body2">{data.model || "-"}</Typography>
                </Grid>

                <Grid item xs={12} sm={6} lg={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Year
                    </Typography>
                    <Typography variant="body2">{data.year || "-"}</Typography>
                </Grid>

                <Grid item xs={12} sm={6} lg={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Type
                    </Typography>
                    <Typography variant="body2">{data.type || "-"}</Typography>
                </Grid>

                <Grid item xs={12} sm={6} lg={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Transmission
                    </Typography>
                    <Typography variant="body2">{data.transmission || "-"}</Typography>
                </Grid>

                <Grid item xs={12} sm={6} lg={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Fuel type
                    </Typography>
                    <Typography variant="body2">{data.fuelType || "-"}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Color
                    </Typography>
                    <Typography variant="body2">{data.color || "-"}</Typography>
                </Grid>

                <Grid item xs={12} sm={6} lg={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Engine No
                    </Typography>
                    <Typography variant="body2">{data.engineNo || "-"}</Typography>
                </Grid>

                <Grid item xs={12} sm={6} lg={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Chassis No
                    </Typography>
                    <Typography variant="body2">{data.chassisNo || "-"}</Typography>
                </Grid>

                <Grid item xs={12} sm={6} lg={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Additional information
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{data.additionalInfo || "-"}</Typography>
                </Grid>

            </Grid>

            {/* Ownership details */}
            <Divider />
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} lg={4}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Current Owner
                    </Typography>
                    <Typography variant="body2">
                        {data.currentOwner?.name || "-"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {data.currentOwner?.phoneNumber || "-"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {data.currentOwner?.email || "-"}
                    </Typography>
                </Grid>

                <Grid item xs={12} sm={6} lg={4}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Owned Since
                    </Typography>
                    <Typography variant="body2">
                        {stringUtils.isoToLocalDateTime(data.currentOwner?.ownershipStartDate) || "-"}
                    </Typography>
                </Grid>

                <Grid item xs={12} sm={6} lg={4}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Ownership History
                    </Typography>
                    {data.previousOwners && data.previousOwners.length > 0 ? (
                        <>
                            <Typography variant="body2" gutterBottom>
                                {data.previousOwners.length} Previous owner{data.previousOwners.length > 1 ? 's' : ''} found
                            </Typography>
                            <Button
                                variant="text"
                                size="small"
                                endIcon={<SearchIcon />}
                                sx={{ textTransform: "none" }} // prevent uppercase
                                onClick={() => setOpenDialog(true)}
                            >
                                View Ownership History
                            </Button>
                        </>
                    ) : (
                        <Typography variant="body2">No previous owners</Typography>
                    )}
                </Grid>
            </Grid>
        </>
    );
}




export default function VehicleDetails() {
    const { id } = useParams();

    return (
        <DetailsView
            title="Vehicle details"
            subtitleAttribute="registrationNumber"
            apiPath={`/tenants/vehicles/${id}`}
            editPath={`/vehicles/${id}/edit`}
            onDelete={(data) => {
                console.log("Delete action for", data);
                // you can plug delete API call here
            }}
        >
            {({ data }) => <VehicleDataLayout data={data} />}

        </DetailsView>
    )
};